import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function Leaderboards() {
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    
    // Regular leaderboards state
    const [leaderboards, setLeaderboards] = useState([]);
    
    // Rollup leaderboards state
    const [rollupLeaderboards, setRollupLeaderboards] = useState([]);
    
    // Combined state
    const [leaderboardType, setLeaderboardType] = useState('regular'); // 'regular' or 'rollup'
    const [selectedLeaderboardID, setSelectedLeaderboardID] = useState('');
    const [scores, setScores] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState(null);
    const refreshTimer = useRef(null);
    const [isTimeBased, setIsTimeBased] = useState(true);
    const [slideshowMode, setSlideshowMode] = useState(false);
    const [isTeamBased, setIsTeamBased] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Helper function to check if scores are team-based
    const checkIfTeamBased = (scoresArray) => {
        if (!Array.isArray(scoresArray) || scoresArray.length === 0) return false;
        
        // Check if TeamName property exists and is populated in at least some scores
        return scoresArray.some(score => score.TeamName && score.TeamName.trim() !== '');
    };
    
    // Helper function to group scores by team
    const groupScoresByTeam = (scoresArray) => {
        if (!Array.isArray(scoresArray) || scoresArray.length === 0) return [];
        
        const teamGroups = {};
        const resultArray = [];
        
        // Group scores by team name
        scoresArray.forEach(score => {
            const teamName = score.TeamName || 'Unassigned';
            if (!teamGroups[teamName]) {
                teamGroups[teamName] = [];
            }
            teamGroups[teamName].push(score);
        });
        
        // Create the hierarchical structure
        Object.keys(teamGroups).forEach(teamName => {
            const teamMembers = teamGroups[teamName];
            if (teamMembers.length > 0) {
                // Add a team row (using the first member's data for the team score)
                const teamData = { 
                    ...teamMembers[0],
                    isTeamRow: true,
                    memberCount: teamMembers.length,
                    teamRowId: `team-${teamName.replace(/\s+/g, '-')}`
                };
                resultArray.push(teamData);
                
                // Add all team members
                teamMembers.forEach(member => {
                    resultArray.push({
                        ...member,
                        isTeamMember: true,
                        parentTeam: teamName
                    });
                });
            }
        });
        
        return resultArray;
    };

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = Array.isArray(data) ? data.filter(event => event.Enabled) : [];
                setEvents(enabledEvents);
                
                // Auto-select first event
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        if (!selectedEventID) return;
        
        // Reset selections and state
        setSelectedLeaderboardID('');
        setLeaderboardType('regular');
        setRefreshInterval(null);
        setSlideshowMode(false);
        setScores([]);
        
        // Fetch regular leaderboards
        const fetchLeaderboards = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllLeaderboards?EventID=${selectedEventID}`);
                const data = await response.json();
                console.log(`Fetched regular leaderboards for EventID ${selectedEventID}:`, data);
                setLeaderboards(Array.isArray(data) ? data.filter(lb => lb.Enabled) : []);
            } catch (error) {
                console.error('Error fetching regular leaderboards:', error);
                setLeaderboards([]);
            }
        };
        
        // Fetch rollup leaderboards
        const fetchRollupLeaderboards = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllRollupLeaderboards?EventID=${selectedEventID}`);
                const data = await response.json();
                console.log(`Fetched rollup leaderboards for EventID ${selectedEventID}:`, data);
                setRollupLeaderboards(Array.isArray(data) ? data.filter(lb => lb.Enabled) : []);
            } catch (error) {
                console.error('Error fetching rollup leaderboards:', error);
                setRollupLeaderboards([]);
            }
        };
        
        // Execute both fetch operations
        Promise.all([fetchLeaderboards(), fetchRollupLeaderboards()]);
    }, [selectedEventID]);

    // Helper function to get the next leaderboard ID in the sequence, considering both types
    const getNextLeaderboardID = useCallback(() => {
        const regularBoards = leaderboards || [];
        const rollupBoards = rollupLeaderboards || [];
        
        // If there are no leaderboards, return empty
        if (regularBoards.length === 0 && rollupBoards.length === 0) return { id: '', type: 'regular' };
        
        // If no leaderboard is selected, start with the first regular one if available
        if (!selectedLeaderboardID) {
            if (regularBoards.length > 0) {
                return { id: regularBoards[0].CompetitionID.toString(), type: 'regular' };
            } else if (rollupBoards.length > 0) {
                return { id: rollupBoards[0].RollupLeaderboardID.toString(), type: 'rollup' };
            }
            return { id: '', type: 'regular' };
        }
        
        // Current selection is a regular leaderboard
        if (leaderboardType === 'regular') {
            const currentIndex = regularBoards.findIndex(lb => lb.CompetitionID === Number(selectedLeaderboardID));
            
            // If we're at the end of regular leaderboards, move to rollup or cycle back
            if (currentIndex === regularBoards.length - 1 || currentIndex === -1) {
                if (rollupBoards.length > 0) {
                    // Move to the first rollup leaderboard
                    return { id: rollupBoards[0].RollupLeaderboardID.toString(), type: 'rollup' };
                } else {
                    // Cycle back to first regular leaderboard
                    return { id: regularBoards[0].CompetitionID.toString(), type: 'regular' };
                }
            } else {
                // Move to next regular leaderboard
                return { 
                    id: regularBoards[currentIndex + 1].CompetitionID.toString(), 
                    type: 'regular' 
                };
            }
        } 
        // Current selection is a rollup leaderboard
        else {
            const currentIndex = rollupBoards.findIndex(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
            
            // If we're at the end of rollup leaderboards, move to regular or cycle back
            if (currentIndex === rollupBoards.length - 1 || currentIndex === -1) {
                if (regularBoards.length > 0) {
                    // Move to the first regular leaderboard
                    return { id: regularBoards[0].CompetitionID.toString(), type: 'regular' };
                } else {
                    // Cycle back to first rollup leaderboard
                    return { id: rollupBoards[0].RollupLeaderboardID.toString(), type: 'rollup' };
                }
            } else {
                // Move to next rollup leaderboard
                return { 
                    id: rollupBoards[currentIndex + 1].RollupLeaderboardID.toString(), 
                    type: 'rollup' 
                };
            }
        }
    }, [leaderboards, rollupLeaderboards, selectedLeaderboardID, leaderboardType]);

    // Handle leaderboard selection change
    const handleLeaderboardChange = (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedLeaderboardID('');
            return;
        }
        
        // Clear existing scores and reset team-based state
        setScores([]);
        setIsTeamBased(false);
        
        const [type, id] = value.split('|');
        setLeaderboardType(type);
        setSelectedLeaderboardID(id);
    };

    // Handle slideshow mode toggle
    const handleSlideshowToggle = (e) => {
        const isChecked = e.target.checked;
        setSlideshowMode(isChecked);
        
        // If enabling slideshow with no leaderboard selected, select the first available one
        if (isChecked && !selectedLeaderboardID) {
            const regularAvailable = leaderboards.length > 0;
            const rollupAvailable = rollupLeaderboards.length > 0;
            
            if (regularAvailable) {
                setLeaderboardType('regular');
                setSelectedLeaderboardID(leaderboards[0].CompetitionID.toString());
            } else if (rollupAvailable) {
                setLeaderboardType('rollup');
                setSelectedLeaderboardID(rollupLeaderboards[0].RollupLeaderboardID.toString());
            }
        }
    };

    useEffect(() => {
        if (!selectedLeaderboardID) return;
        
        // Clear existing scores and set loading state
        setScores([]);
        setIsLoading(true);
        
        if (leaderboardType === 'regular') {
            const selectedLeaderboard = leaderboards.find(lb => lb.CompetitionID === Number(selectedLeaderboardID));
            if (selectedLeaderboard) {
                setIsTimeBased(selectedLeaderboard.TimeBased);
                setRefreshInterval(selectedLeaderboard.RefreshInterval || 30);
                fetchRegularScores(selectedLeaderboardID);
            }
        } else { // rollup
            const selectedRollupLeaderboard = rollupLeaderboards.find(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
            if (selectedRollupLeaderboard) {
                setRefreshInterval(selectedRollupLeaderboard.RefreshInterval || 30);
                fetchRollupScores(selectedLeaderboardID);
            }
        }
    }, [selectedLeaderboardID, leaderboardType, leaderboards, rollupLeaderboards]);

    useEffect(() => {
        if (refreshInterval && selectedLeaderboardID) {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
            }
            
            refreshTimer.current = setInterval(async () => {
                if (slideshowMode) {
                    // In slideshow mode, move to the next leaderboard
                    const next = getNextLeaderboardID();
                    console.log(`Slideshow: switching to ${next.type} leaderboard ${next.id}`);
                    setLeaderboardType(next.type);
                    setSelectedLeaderboardID(next.id);
                } else {
                    // Normal refresh mode - refresh current leaderboard
                    if (leaderboardType === 'regular') {
                        await fetchRegularScores(selectedLeaderboardID);
                    } else {
                        await fetchRollupScores(selectedLeaderboardID);
                    }
                }
            }, refreshInterval * 1000);

            return () => {
                clearInterval(refreshTimer.current);
            };
        }
    }, [refreshInterval, selectedLeaderboardID, leaderboardType, leaderboards, rollupLeaderboards, slideshowMode, getNextLeaderboardID]);

    const fetchRegularScores = async (leaderboardID) => {
        try {
            console.log(`Fetching regular scores for leaderboard: ${leaderboardID}`);
            setIsLoading(true);
            
            // Reset state before fetching new data
            setScores([]);
            setIsTeamBased(false);
            
            const response = await fetch(`${API_BASE_URL}/getLeaderboardScores?CompetitionID=${leaderboardID}`);
            const data = await response.json();
            const scoresArray = Array.isArray(data) ? data : [];
            
            // Check if the leaderboard is team-based
            const teamBased = checkIfTeamBased(scoresArray);
            setIsTeamBased(teamBased);
            
            // If team-based, group by teams
            if (teamBased) {
                setScores(groupScoresByTeam(scoresArray));
            } else {
                setScores(scoresArray);
            }
        } catch (error) {
            console.error('Error fetching regular scores:', error);
            setScores([]);
            setIsTeamBased(false);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchRollupScores = async (rollupLeaderboardID) => {
        try {
            console.log(`Fetching rollup scores for leaderboard: ${rollupLeaderboardID}`);
            setIsLoading(true);
            
            // Reset state before fetching new data
            setScores([]);
            setIsTeamBased(false);
            
            const response = await fetch(`${API_BASE_URL}/getRollupLeaderboardScores?rollupLeaderboardID=${rollupLeaderboardID}`);
            const data = await response.json();
            setScores(Array.isArray(data) ? data : []);
            setIsTeamBased(false); // Rollup leaderboards aren't team-based currently
        } catch (error) {
            console.error('Error fetching rollup scores:', error);
            setScores([]);
            setIsTeamBased(false);
        } finally {
            setIsLoading(false);
        }
    };

    const displayValue = (value) => (value === null || value === undefined || value === '' ? '-' : value);

    const formatTime = (minutes, seconds) => {
        if ((minutes === null || minutes === undefined) && (seconds === null || seconds === undefined)) return '-';
        const mm = minutes !== null && minutes !== undefined ? String(minutes).padStart(2, '0') : '00';
        if (seconds === null || seconds === undefined) return `${mm}:00`;
        const [secInt, secDec] = String(seconds).split('.');
        const ss = secInt.padStart(2, '0');
        return secDec !== undefined ? `${mm}:${ss}.${secDec}` : `${mm}:${ss}`;
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined || num === '') return '-';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const getRollupRankColumns = (scoresArray) => {
        if (!Array.isArray(scoresArray) || scoresArray.length === 0) return [];
        return Object.keys(scoresArray[0]).filter(key => key.startsWith('Rank in '));
    };

    const getRollupMetricLabel = () => {
        if (leaderboardType !== 'rollup') return 'Rollup Points';
        const selectedRollup = rollupLeaderboards.find(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
        return selectedRollup?.RollupMethod === 'Average' ? 'Average Score' : 'Rollup Points';
    };

    // Helper function to check if a column should be visible based on leaderboard settings
    const shouldShowColumn = (column) => {
        if (leaderboardType === 'rollup') {
            const selectedRollup = rollupLeaderboards.find(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
            if (!selectedRollup) {
                return scores.some(score => score[column] !== null && score[column] !== undefined);
            }

            if (column.startsWith('Rank in ')) {
                return selectedRollup.ShowCompetitionRanks !== false;
            }

            switch (column) {
                case 'CompetitorID':
                    return selectedRollup.ShowCompetitorID !== false;
                case 'CompetitorName':
                    return selectedRollup.ShowCompetitorName !== false;
                case 'Organization':
                    return selectedRollup.ShowOrganization !== false;
                case 'CompetitorState':
                    return selectedRollup.ShowCompetitorState !== false;
                case 'Division':
                    return selectedRollup.ShowDivision !== false;
                case 'TotalRollupPoints':
                    return selectedRollup.ShowRollupPoints !== false;
                default:
                    return scores.some(score => score[column] !== null && score[column] !== undefined);
            }
        }
        
        // Get the current selected regular leaderboard
        const selectedLB = leaderboards.find(lb => lb.CompetitionID === Number(selectedLeaderboardID));
        if (!selectedLB) return true; // Default to showing if settings not found
        
        // Check column visibility based on the settings in the leaderboard
        switch(column) {
            case 'CompetitorID': 
                return selectedLB.ShowCompetitorID !== false;
            case 'CompetitorName': 
                return selectedLB.ShowCompetitorName !== false;
            case 'Organization': 
                return selectedLB.ShowOrganization !== false;
            case 'CompetitorState': 
                return selectedLB.ShowCompetitorState !== false;
            case 'Division': 
                return selectedLB.ShowDivision !== false;
            case 'AttemptNumber': 
                return selectedLB.ShowAttempt !== false;
            case 'Minutes':
            case 'Seconds': 
                return selectedLB.ShowTime !== false && isTimeBased;
            case 'BaseScore': 
                return selectedLB.ShowBaseScore !== false && (!isTimeBased || scores.some(score => score.BaseScore != null));
            case 'BonusesPenalties': 
                return selectedLB.ShowBonusesPenalties !== false && (!isTimeBased || scores.some(score => score.BonusesPenalties != null));
            case 'FinalScore': 
                return true; // Always show final score
            default: 
                return scores.some(score => score[column] != null); // Show if data exists
        }
    };

    const shouldHideColumn = (column) => {
        // First check if column should be shown based on leaderboard settings
        if (!shouldShowColumn(column)) return true;
        
        if (leaderboardType === 'rollup') {
            // Different logic for rollup leaderboards
            return scores.every(score => score[column] === null || score[column] === undefined);
        }
        
        // Regular leaderboard logic
        if (!isTimeBased && ['Minutes', 'Seconds', 'BaseScore', 'BonusesPenalties'].includes(column)) {
            return true; // Hide time-related columns if TimeBased is false
        }
        
        // Fall back to checking if data exists
        return scores.every(score => score[column] === null);
    };

    // Get the current leaderboard name based on type and ID
    const getLeaderboardName = () => {
        if (!selectedLeaderboardID) return '';
        
        if (leaderboardType === 'regular') {
            const lb = leaderboards.find(lb => lb.CompetitionID === Number(selectedLeaderboardID));
            return lb?.CompetitionName || '';
        } else {
            const lb = rollupLeaderboards.find(lb => lb.RollupLeaderboardID === Number(selectedLeaderboardID));
            return lb?.RollupLeaderboardName || '';
        }
    };

    // Helper function to render competitor info in a compact format
    const renderCompactCompetitorInfo = (score) => {
        if (score.isTeamRow) return '';
        
        const details = [];
        
        if (!shouldHideColumn('Organization') && score.Organization) {
            details.push(score.Organization);
        }
        
        if (!shouldHideColumn('CompetitorState') && score.CompetitorState) {
            details.push(score.CompetitorState);
        }
        
        if (!shouldHideColumn('Division') && score.Division) {
            details.push(score.Division);
        }
        
        return (
            <div>
                <div className="competitor-name">{score.CompetitorName}</div>
                {details.length > 0 && (
                    <div className="competitor-details">{details.join(' • ')}</div>
                )}
            </div>
        );
    };

    // Render table based on leaderboard type (desktop)
    const renderTable = () => {
        if (leaderboardType === 'rollup') {
            // Rollup leaderboard table
            const isCompetitorBased = scores[0]?.CompetitorID !== undefined;
            const isOrganizationBased = scores[0]?.Organization !== undefined;
            const rankColumns = getRollupRankColumns(scores);
            
            return (
                <table className="data-table">
                    <thead>
                        {isCompetitorBased ? (
                            <tr>
                                <th>Rank</th>
                                {!shouldHideColumn('CompetitorID') && <th>ID</th>}
                                {!shouldHideColumn('CompetitorName') && <th>Name</th>}
                                {!shouldHideColumn('Organization') && <th>Organization</th>}
                                {!shouldHideColumn('CompetitorState') && <th>State</th>}
                                {!shouldHideColumn('Division') && <th>Division</th>}
                                {!shouldHideColumn('TotalRollupPoints') && <th>{getRollupMetricLabel()}</th>}
                                {rankColumns.filter(column => !shouldHideColumn(column)).map(column => (
                                    <th key={column}>{column}</th>
                                ))}
                            </tr>
                        ) : isOrganizationBased ? (
                            <tr>
                                <th>Rank</th>
                                {!shouldHideColumn('Organization') && <th>Organization</th>}
                                {!shouldHideColumn('TotalRollupPoints') && <th>{getRollupMetricLabel()}</th>}
                                {rankColumns.filter(column => !shouldHideColumn(column)).map(column => (
                                    <th key={column}>{column}</th>
                                ))}
                            </tr>
                        ) : null}
                    </thead>
                    <tbody>
                        {scores.map((score, index) =>
                            isCompetitorBased ? (
                                <tr key={score.CompetitorID || index} style={{ borderBottom: '2px solid #000' }}>
                                    <td>{displayValue(score.OverallRank)}</td>
                                    {!shouldHideColumn('CompetitorID') && <td>{displayValue(score.CompetitorID)}</td>}
                                    {!shouldHideColumn('CompetitorName') && <td>{displayValue(score.CompetitorName)}</td>}
                                    {!shouldHideColumn('Organization') && <td>{displayValue(score.Organization)}</td>}
                                    {!shouldHideColumn('CompetitorState') && <td>{displayValue(score.CompetitorState)}</td>}
                                    {!shouldHideColumn('Division') && <td>{displayValue(score.Division)}</td>}
                                    {!shouldHideColumn('TotalRollupPoints') && <td>{formatNumber(score.TotalRollupPoints)}</td>}
                                    {rankColumns.filter(column => !shouldHideColumn(column)).map(column => (
                                        <td key={column}>{displayValue(score[column])}</td>
                                    ))}
                                </tr>
                            ) : isOrganizationBased ? (
                                <tr key={index} style={{ borderBottom: '2px solid #000' }}>
                                    <td>{displayValue(score.OverallRank)}</td>
                                    {!shouldHideColumn('Organization') && <td>{displayValue(score.Organization)}</td>}
                                    {!shouldHideColumn('TotalRollupPoints') && <td>{formatNumber(score.TotalRollupPoints)}</td>}
                                    {rankColumns.filter(column => !shouldHideColumn(column)).map(column => (
                                        <td key={column}>{displayValue(score[column])}</td>
                                    ))}
                                </tr>
                            ) : null
                        )}
                    </tbody>
                </table>
            );
        } else {
            // Regular leaderboard table with team-based display support
            return (
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Rank</th>
                            {isTeamBased && <th>Team</th>}
                            {!shouldHideColumn('CompetitorID') && <th>ID</th>}
                            {!shouldHideColumn('CompetitorName') && <th>Name</th>}
                            {!shouldHideColumn('AttemptNumber') && <th>Attempt #</th>}
                            {!shouldHideColumn('Minutes') && !shouldHideColumn('Seconds') && <th>Time</th>}
                            {!shouldHideColumn('BaseScore') && <th>Seconds</th>}
                            {!shouldHideColumn('BonusesPenalties') && <th>Bonuses/Penalties ±</th>}
                            {!shouldHideColumn('FinalScore') && <th>Score</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {scores.map((score, index) => {
                            // Style for team rows
                            const rowStyle = score.isTeamRow ? {
                                backgroundColor: '#e6f7ff',
                                fontWeight: 'bold',
                                borderBottom: '1px solid #a8d4f5',
                                borderTop: '2px solid #0082d4'
                            } : score.isTeamMember ? {
                                paddingLeft: '20px',
                                backgroundColor: '#f9fdff',
                                borderBottom: '1px solid #eaeaea'
                            } : {
                                borderBottom: '2px solid #000'
                            };
                            
                            return (
                                <tr key={score.isTeamRow ? score.teamRowId : (score.CompetitorID || index)} style={rowStyle}>
                                    <td>{score.isTeamMember ? '' : displayValue(score.Placement)}</td>
                                    {isTeamBased && (
                                        <td>
                                            {score.isTeamRow ? (
                                                <strong>{score.TeamName} ({score.memberCount})</strong>
                                            ) : score.isTeamMember ? (
                                                <span className="team-member-indicator">→</span>
                                            ) : displayValue(score.TeamName)}
                                        </td>
                                    )}
                                    {!shouldHideColumn('CompetitorID') && (
                                        <td>{score.isTeamRow ? '' : displayValue(score.CompetitorID)}</td>
                                    )}
                                    {!shouldHideColumn('CompetitorName') && (
                                        <td className="competitor-column">{renderCompactCompetitorInfo(score)}</td>
                                    )}
                                    {!shouldHideColumn('AttemptNumber') && (
                                        <td>{score.isTeamMember ? '' : displayValue(score.AttemptNumber)}</td>
                                    )}
                                    {!shouldHideColumn('Minutes') && !shouldHideColumn('Seconds') && (
                                        <td>{score.isTeamMember ? '' : formatTime(score.Minutes, score.Seconds)}</td>
                                    )}
                                    {!shouldHideColumn('BaseScore') && (
                                        <td>{score.isTeamMember ? '' : formatNumber(score.BaseScore)}</td>
                                    )}
                                    {!shouldHideColumn('BonusesPenalties') && (
                                        <td>{score.isTeamMember ? '' : formatNumber(score.BonusesPenalties)}</td>
                                    )}
                                    {!shouldHideColumn('FinalScore') && (
                                        <td>{score.isTeamMember ? '' : formatNumber(score.FinalScore)}</td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        }
    };

    const renderDetailRows = (detailItems) => {
        if (detailItems.length === 0) return null;

        return (
            <details className="mobile-card-details">
                <summary className="mobile-card-summary">
                    <span className="toggle-more">More +</span>
                    <span className="toggle-less">Less –</span>
                </summary>
                <div className="mobile-card-details-body">
                    {detailItems.map((item) => (
                        <div className="mobile-card-row" key={item.label}>
                            <span className="mobile-card-label">{item.label}</span>
                            <span className="mobile-card-value">{item.value}</span>
                        </div>
                    ))}
                </div>
            </details>
        );
    };

    const renderRegularCard = (score, index) => {
        const detailItems = [];

        if (!shouldHideColumn('AttemptNumber')) {
            detailItems.push({ label: 'Attempt #', value: displayValue(score.AttemptNumber) });
        }
        if (!shouldHideColumn('Minutes') && !shouldHideColumn('Seconds')) {
            detailItems.push({ label: 'Time', value: formatTime(score.Minutes, score.Seconds) });
        }
        if (!shouldHideColumn('BaseScore')) {
            detailItems.push({ label: 'Seconds', value: formatNumber(score.BaseScore) });
        }
        if (!shouldHideColumn('BonusesPenalties')) {
            detailItems.push({ label: 'Bonuses/Penalties ±', value: formatNumber(score.BonusesPenalties) });
        }

        const showCompetitorId = !shouldHideColumn('CompetitorID') && !score.isTeamMember;
        const showCompetitorName = !shouldHideColumn('CompetitorName') && !score.isTeamRow;
        const showOrg = !shouldHideColumn('Organization') && score.Organization;
        const showState = !shouldHideColumn('CompetitorState') && score.CompetitorState;
        const showDivision = !shouldHideColumn('Division') && score.Division;
        const showTotalScore = !shouldHideColumn('FinalScore') && !score.isTeamMember;

        const details = [showOrg ? score.Organization : null, showState ? score.CompetitorState : null, showDivision ? score.Division : null]
            .filter(Boolean)
            .join(' • ');

        return (
            <div className="mobile-card" key={score.isTeamRow ? score.teamRowId : (score.CompetitorID || index)}>
                <div className="mobile-card-header">
                    <div className="mobile-card-rank">
                        {score.isTeamMember ? '' : displayValue(score.Placement)}
                    </div>
                    <div className="mobile-card-title">
                        {score.isTeamRow
                            ? `${score.TeamName} (${score.memberCount})`
                            : showCompetitorName
                                ? displayValue(score.CompetitorName)
                                : showCompetitorId
                                    ? `ID: ${displayValue(score.CompetitorID)}`
                                    : ''}
                        {showCompetitorId && showCompetitorName && (
                            <div className="mobile-card-subtitle">ID: {displayValue(score.CompetitorID)}</div>
                        )}
                    </div>
                    {showTotalScore && (
                        <div className="mobile-card-score">
                            <span className="mobile-card-score-label">Score:</span>
                            {formatNumber(score.FinalScore)}
                        </div>
                    )}
                </div>

                {details && !score.isTeamRow && (
                    <div className="mobile-card-meta">{details}</div>
                )}

                {renderDetailRows(detailItems)}
            </div>
        );
    };

    const renderTeamCard = (teamScore, teamMembers, index) => {
        const detailItems = [];

        if (!shouldHideColumn('AttemptNumber')) {
            detailItems.push({ label: 'Attempt #', value: displayValue(teamScore.AttemptNumber) });
        }
        if (!shouldHideColumn('Minutes') && !shouldHideColumn('Seconds')) {
            detailItems.push({ label: 'Time', value: formatTime(teamScore.Minutes, teamScore.Seconds) });
        }
        if (!shouldHideColumn('BaseScore')) {
            detailItems.push({ label: 'Seconds', value: formatNumber(teamScore.BaseScore) });
        }
        if (!shouldHideColumn('BonusesPenalties')) {
            detailItems.push({ label: 'Bonuses/Penalties ±', value: formatNumber(teamScore.BonusesPenalties) });
        }

        const showTotalScore = !shouldHideColumn('FinalScore');
        const showMemberId = !shouldHideColumn('CompetitorID');
        const showMemberName = !shouldHideColumn('CompetitorName');

        return (
            <div className="mobile-card" key={teamScore.teamRowId || index}>
                <div className="mobile-card-header">
                    <div className="mobile-card-rank">{displayValue(teamScore.Placement)}</div>
                    <div className="mobile-card-title">
                        {teamScore.TeamName} ({teamScore.memberCount})
                    </div>
                    {showTotalScore && (
                        <div className="mobile-card-score">
                            <span className="mobile-card-score-label">Score:</span>
                            {formatNumber(teamScore.FinalScore)}
                        </div>
                    )}
                </div>

                {renderDetailRows(detailItems)}

                {teamMembers.length > 0 && (showMemberId || showMemberName) && (
                    <div className="mobile-team-member-list">
                        {teamMembers.map((member, memberIndex) => (
                            <div className="mobile-team-member-row" key={member.CompetitorID || memberIndex}>
                                {showMemberId && (
                                    <span className="mobile-team-member-id">{displayValue(member.CompetitorID)}</span>
                                )}
                                {showMemberName && (
                                    <span className="mobile-team-member-name">{displayValue(member.CompetitorName)}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderRollupCard = (score, index) => {
        const rankColumns = getRollupRankColumns(scores).filter(column => !shouldHideColumn(column));
        const detailItems = [];

        const showRollupName = !shouldHideColumn('CompetitorName') && score.CompetitorName;
        const showRollupOrg = !shouldHideColumn('Organization') && score.Organization;
        const showRollupId = !shouldHideColumn('CompetitorID') && score.CompetitorID;

        if (!shouldHideColumn('TotalRollupPoints')) {
            detailItems.push({ label: getRollupMetricLabel(), value: formatNumber(score.TotalRollupPoints) });
        }

        return (
            <div className="mobile-card" key={score.CompetitorID || score.Organization || index}>
                <div className="mobile-card-header">
                    <div className="mobile-card-rank">{displayValue(score.OverallRank)}</div>
                    <div className="mobile-card-title">
                        {showRollupName && (
                            <div>{displayValue(score.CompetitorName)}</div>
                        )}
                        {showRollupOrg && (
                            <div>{displayValue(score.Organization)}</div>
                        )}
                        {showRollupId && (showRollupName || showRollupOrg) && (
                            <div className="mobile-card-subtitle">ID: {displayValue(score.CompetitorID)}</div>
                        )}
                        {!showRollupName && !showRollupOrg && showRollupId && (
                            <div>ID: {displayValue(score.CompetitorID)}</div>
                        )}
                        {!shouldHideColumn('CompetitorState') && score.CompetitorState && (
                            <div className="mobile-card-meta">{displayValue(score.CompetitorState)}</div>
                        )}
                        {!shouldHideColumn('Division') && score.Division && (
                            <div className="mobile-card-meta">{displayValue(score.Division)}</div>
                        )}
                    </div>
                </div>

                {(rankColumns.length > 0 || detailItems.length > 0) && (
                    <details className="mobile-card-details">
                        <summary className="mobile-card-summary">
                            <span className="toggle-more">More +</span>
                            <span className="toggle-less">Less –</span>
                        </summary>
                        <div className="mobile-card-details-body">
                            {detailItems.map((item) => (
                                <div className="mobile-card-row" key={item.label}>
                                    <span className="mobile-card-label">{item.label}</span>
                                    <span className="mobile-card-value">{item.value}</span>
                                </div>
                            ))}
                            {rankColumns.length > 0 && (
                                <div className="mobile-card-grid">
                                    <div className="mobile-card-grid-header">Competition</div>
                                    <div className="mobile-card-grid-header">Rank</div>
                                    {rankColumns.map((column) => (
                                        <React.Fragment key={column}>
                                            <div className="mobile-card-grid-cell">{column.replace('Rank in ', '')}</div>
                                            <div className="mobile-card-grid-cell">{displayValue(score[column])}</div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                    </details>
                )}
            </div>
        );
    };

    const renderMobileCards = () => {
        if (leaderboardType === 'rollup') {
            return scores.map((score, index) => renderRollupCard(score, index));
        }

        if (isTeamBased) {
            const teamCards = [];
            let currentTeam = null;

            scores.forEach((score) => {
                if (score.isTeamRow) {
                    currentTeam = { team: score, members: [] };
                    teamCards.push(currentTeam);
                } else if (score.isTeamMember && currentTeam && score.parentTeam === currentTeam.team.TeamName) {
                    currentTeam.members.push(score);
                } else if (!score.isTeamMember) {
                    teamCards.push({ solo: score });
                }
            });

            return teamCards.map((entry, index) => {
                if (entry.team) {
                    return renderTeamCard(entry.team, entry.members, index);
                }

                return renderRegularCard(entry.solo, index);
            });
        }

        return scores.map((score, index) => renderRegularCard(score, index));
    };

    // Create combined leaderboard options
    const getLeaderboardOptions = () => {
        const regularOptions = Array.isArray(leaderboards) ? leaderboards.map(lb => ({
            value: `regular|${lb.CompetitionID}`,
            label: `🏆 ${lb.CompetitionName}`
        })) : [];
        
        const rollupOptions = Array.isArray(rollupLeaderboards) ? rollupLeaderboards.map(lb => ({
            value: `rollup|${lb.RollupLeaderboardID}`,
            label: `📊 ${lb.RollupLeaderboardName}`
        })) : [];
        
        return [...regularOptions, ...rollupOptions];
    };

    return (
        <div className="p-1">
            <h2>
                Leaderboard
                {selectedLeaderboardID && ` - ${getLeaderboardName()}`}
            </h2>
            
            <div className="leaderboard-filters">
                <label>
                    <select className="form-select" value={selectedEventID} onChange={(e) => setSelectedEventID(e.target.value)}>
                        <option value="">Select Event</option>
                        {Array.isArray(events) && events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>
                
                <label>
                    <select 
                        className="form-select"
                        value={selectedLeaderboardID ? `${leaderboardType}|${selectedLeaderboardID}` : ''}
                        onChange={handleLeaderboardChange}
                        disabled={!selectedEventID}
                    >
                        <option value="">Select Leaderboard</option>
                        {getLeaderboardOptions().map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
                
                {selectedEventID && (leaderboards.length > 0 || rollupLeaderboards.length > 0) && (
                    <label className="slideshow-toggle">
                        <input 
                            type="checkbox" 
                            className="form-checkbox"
                            checked={slideshowMode}
                            onChange={handleSlideshowToggle}
                        />
                        Slideshow Mode
                    </label>
                )}
            </div>
            
            {selectedLeaderboardID && (
                <div className="mt-1">
                    {isLoading ? (
                        <p>Loading scores...</p>
                    ) : scores.length === 0 ? (
                        <p>No scores available</p>
                    ) : (
                        <>
                            <div className="data-table-wrapper desktop-table">
                                {renderTable()}
                            </div>
                            <div className="mobile-cards">
                                {renderMobileCards()}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}